import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Icon from "../../components/Icon";
import { StatusBadge } from "../../components/Badges";
import LoadingBlock from "../../components/LoadingBlock";
import EmptyState from "../../components/EmptyState";
import Drawer from "../../components/Drawer";
import TaskDrawer from "../../components/TaskDrawer";
import BusinessDrivePanel from "../../components/BusinessDrivePanel";
import BusinessPaymentsPanel from "../../components/BusinessPaymentsPanel";
import Breadcrumbs from "../../components/Breadcrumbs";
import SearchableSelect from "../../components/SearchableSelect";
import { useApi } from "../../hooks/useApi";
import useScopedBase from "../../hooks/useScopedBase";
import { useAuth } from "../../contexts/AuthContext";
import {
  getBusiness,
  createBusinessLogin,
  resetBusinessLoginPassword,
  removeBusinessLogin,
  getBusinessActivity,
  listBusinessFieldGroups,
  createBusinessFieldGroup,
  importBusinessFieldGroups,
  listAllBusinesses,
  listBusinessTasks,
  assignBusinessTask,
  updateBusinessTaskPriority,
  updateBusinessTask,
  removeBusinessTask,
} from "../../api/businessService";
import { listFirmProfiles } from "../../api/firmProfileService";
import {
  listFields,
  createField,
  updateFieldGroup,
  deleteFieldGroup,
} from "../../api/fieldGroupService";
import { updateField, deleteField } from "../../api/fieldService";
import {
  listBusinessNotes,
  createBusinessNote,
  updateBusinessNote,
  deleteBusinessNote,
} from "../../api/businessNoteService";
import { listEmployeeTasks } from "../../api/employeeTaskService";
import { listTaskMasters } from "../../api/taskMasterService";
import { paginatedRows } from "../../utils/pagination";
import { formatDateShort, formatDateTime, formatINRExact, initials, serialNumber } from "../../utils/formatters";
import { showApiError, showToast } from "../../utils/toast";
import { showCredentials } from "../../utils/credentials";

export default function BusinessDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const base = useScopedBase();
  const { hasPermission, role, user } = useAuth();
  // Per-action perms. Sensitive actions like attaching a task master to a
  // business stay admin-only because they trigger task instance generation.
  const canAdd = hasPermission("client.add");
  const canEdit = hasPermission("client.edit");
  const canDelete = role === "admin";
  // Admins manage everything (recurring attach/detach, priority cascade);
  // employees with client.task.oneoff can attach/detach one-off tasks only.
  const canManageTaskAssignments = role === "admin";
  const canAttachOneOff = canManageTaskAssignments || hasPermission("client.task.oneoff");
  // Granting a customer login provisions a User account — admin-only.
  const canGrantLogin = role === "admin";
  // null | "create" | "reset" — drives the grant/reset login drawer.
  const [loginMode, setLoginMode] = useState(null);
  const [removingLogin, setRemovingLogin] = useState(false);
  const [tab, setTab] = useState("profile");
  // Live attached-payment-template count, reported up by BusinessPaymentsPanel
  // once the tab is opened. Until then the tab badge falls back to the
  // payment_masters_count shipped by the business `show` endpoint.
  const [paymentCount, setPaymentCount] = useState(null);
  const [openTask, setOpenTask] = useState(null);
  const [openGroups, setOpenGroups] = useState({});
  const [revealed, setRevealed] = useState({});
  const [openCreateGroup, setOpenCreateGroup] = useState(false);
  const [openImportGroups, setOpenImportGroups] = useState(false);
  const [openAssignTask, setOpenAssignTask] = useState(false);
  // The attached recurring task whose priority / auto-invoicing is being edited.
  const [editingAttachment, setEditingAttachment] = useState(null);
  const [savingAttachment, setSavingAttachment] = useState(false);
  // Per-field reveal toggles for the encrypted credential columns shown in
  // the Profile tab. Key is the field name (e.g. "it_password").
  const [revealProfilePw, setRevealProfilePw] = useState({});

  const biz = useApi(() => getBusiness(id), [id]);
  const fieldGroups = useApi(() => listBusinessFieldGroups(id), [id]);
  const assignedMasters = useApi(() => listBusinessTasks(id), [id]);
  const employeeTasks = useApi(
    () => listEmployeeTasks({ business_id: id, per_page: 50 }),
    [id],
  );
  const notesApi = useApi(() => listBusinessNotes(id, { per_page: 100 }), [id]);

  const b = biz.data?.data;
  const groupRows = paginatedRows(fieldGroups.data);
  const noteRows = paginatedRows(notesApi.data);
  // BusinessTaskAssignmentController returns a flat array (not paginated).
  const assignedRows = Array.isArray(assignedMasters.data?.data)
    ? assignedMasters.data.data
    : [];
  const taskRows = paginatedRows(employeeTasks.data);

  if (biz.loading || !b) return <LoadingBlock rows={3} height={80} />;

  return (
    <>
      <Breadcrumbs
        items={[
          { label: "Clients", to: `${base}/clients` },
          { label: b.business_name },
        ]}
      />
      <div className="page-head">
        <div>
          <button className="btn btn-ghost btn-sm" onClick={() => navigate(-1)} style={{ marginLeft: -8, marginBottom: 8 }}>
            <Icon name="chevron-left" size={14} /> Back
          </button>
          <div className="row gap-md">
            <div className="avatar avatar-lg" style={{ background: `hsl(${(b.id * 47) % 360} 60% 55%)`, fontSize: 16 }}>
              {initials(b.client_name || b.business_name)}
            </div>
            <div>
              <h1 className="page-title">{b.business_name}</h1>
              <div className="page-sub row gap-sm" style={{ marginTop: 6, flexWrap: "wrap", alignItems: "center" }}>
                {b.client_name && (
                  <>
                    <span className="t-primary" title="Client name">
                      <Icon name="user" size={12} style={{ verticalAlign: "middle", marginRight: 4 }} />
                      {b.client_name}
                    </span>
                    <span>·</span>
                  </>
                )}
                {b.client_id && (
                  <>
                    <span className="t-mono fw-600" title="Client ID">{b.client_id}</span>
                    <span>·</span>
                  </>
                )}
                {b.is_active ? (
                  <span className="badge badge-success">Active</span>
                ) : (
                  <span className="badge badge-neutral">Inactive</span>
                )}
                {b.user_id ? (
                  <span className="badge badge-info">Login enabled</span>
                ) : (
                  <span className="badge badge-warning">No login</span>
                )}
                {b.business_email && (<><span>·</span><span>{b.business_email}</span></>)}
                {b.business_phone && (<><span>·</span><span>{b.business_phone}</span></>)}
              </div>
            </div>
          </div>
        </div>
        <div className="page-actions">
          {!b.user_id && canGrantLogin && (
            <button
              className="btn btn-secondary"
              onClick={() => setLoginMode("create")}
              title="Create a portal login for this client"
            >
              <Icon name="user" size={14} /> Grant login
            </button>
          )}
          {b.user_id && canGrantLogin && (
            <button
              className="btn btn-secondary"
              onClick={() => setLoginMode("reset")}
              title="Reset the client's portal password"
            >
              <Icon name="key" size={14} /> Reset password
            </button>
          )}
          {b.user_id && canGrantLogin && (
            <button
              className="btn btn-secondary"
              disabled={removingLogin}
              style={{ color: "var(--danger)" }}
              onClick={async () => {
                if (!window.confirm(
                  `Deactivate the customer login for ${b.business_name}?\n\nThis deletes their portal account — they will no longer be able to sign in, and their support tickets will be removed. The client record, drive, fields and tasks are kept. You can grant a new login later.`,
                )) return;
                setRemovingLogin(true);
                try {
                  await removeBusinessLogin(b.id);
                  showToast("success", "Login deactivated");
                  biz.reload();
                } catch (err) {
                  showApiError(err);
                } finally {
                  setRemovingLogin(false);
                }
              }}
            >
              <Icon name="log-out" size={14} /> {removingLogin ? "Deactivating…" : "Deactivate login"}
            </button>
          )}
          {tab === "fields" && canAdd && (
            <>
              <button className="btn btn-secondary" onClick={() => setOpenImportGroups(true)}>
                <Icon name="layers" size={14} /> Import from business
              </button>
              <button className="btn btn-primary" onClick={() => setOpenCreateGroup(true)}>
                <Icon name="plus" size={14} /> Add field group
              </button>
            </>
          )}
          {tab === "tasks" && canAttachOneOff && (
            <button className="btn btn-primary" onClick={() => setOpenAssignTask(true)}>
              <Icon name="plus" size={14} /> Assign task master
            </button>
          )}
        </div>
      </div>

      <div className="grid-stack-300">
        <div>
          <div className="tabs">
            {[
              { id: "profile", label: "Profile", icon: "user" },
              { id: "fields", label: "Field Groups", icon: "lock", count: groupRows.length },
              { id: "tasks", label: "Assigned Tasks", icon: "check-square", count: assignedRows.length },
              { id: "instances", label: "Task instances", icon: "layers", count: taskRows.length },
              { id: "payments", label: "Payments", icon: "dollar-sign", count: paymentCount ?? b.payment_masters_count ?? 0 },
              { id: "notes", label: "Notes", icon: "file-text", count: noteRows.length },
              { id: "drive", label: "Drive", icon: "folder" },
              ...(role === "admin" ? [{ id: "activity", label: "Activity", icon: "clock" }] : []),
            ].map((t) => (
              <button key={t.id} className={`tab ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>
                <Icon name={t.icon} size={13} /> {t.label}
                {t.count !== undefined && <span className="tab-count">{t.count}</span>}
              </button>
            ))}
          </div>

          {tab === "profile" && (
            <ProfileTab
              b={b}
              revealed={revealProfilePw}
              setRevealed={setRevealProfilePw}
            />
          )}

          {tab === "fields" && (
            <div className="col gap-md">
              {fieldGroups.loading ? (
                <LoadingBlock rows={2} height={120} />
              ) : groupRows.length === 0 ? (
                <EmptyState icon="lock" title="No field groups" hint="Add a field group to store credentials and metadata." />
              ) : (
                groupRows.map((g) => (
                  <FieldGroupCard
                    key={g.id}
                    group={g}
                    open={openGroups[g.id] !== false}
                    onToggle={() => setOpenGroups({ ...openGroups, [g.id]: !(openGroups[g.id] !== false) })}
                    revealed={revealed}
                    setRevealed={setRevealed}
                    onChanged={() => fieldGroups.reload()}
                    canAdd={canAdd}
                    canEdit={canEdit}
                    canDelete={canDelete}
                  />
                ))
              )}
            </div>
          )}

          {tab === "tasks" && (
            <div className="card">
              <div className="card-body no-pad">
                {assignedMasters.loading ? (
                  <div style={{ padding: 16 }}><LoadingBlock rows={3} /></div>
                ) : assignedRows.length === 0 ? (
                  <EmptyState
                    icon="check-square"
                    title="No task masters attached"
                    hint="Attach a task master so instances can be auto-generated."
                  />
                ) : (
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ width: 50 }} className="t-num">#</th>
                        <th>Task master</th>
                        <th>Group</th>
                        <th>Recurrence</th>
                        <th>Billing</th>
                        <th>Priority</th>
                        <th style={{ width: 180 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {assignedRows.map((m, i) => (
                        <tr key={m.id}>
                          <td className="t-num t-muted">{serialNumber(null, i)}</td>
                          <td className="t-primary">{m.name}</td>
                          <td><span className="badge badge-neutral">{m.task_group?.name || "—"}</span></td>
                          <td className="t-muted">
                            {m.is_recurring ? (m.recurrence_pattern || "—") : "One-off"}
                          </td>
                          <td>
                            {m.pivot?.auto_invoice ? (
                              <span className="badge badge-success" title={m.pivot?.auto_invoice_description || ""}>
                                Auto-invoice {formatINRExact(m.pivot?.auto_invoice_amount)}
                              </span>
                            ) : (
                              <span className="t-muted">—</span>
                            )}
                          </td>
                          <td>
                            <AttachedTaskPriorityCell
                              businessId={id}
                              taskMasterId={m.id}
                              value={m.pivot?.priority}
                              canEdit={canManageTaskAssignments || (canAttachOneOff && !m.is_recurring)}
                              onChanged={() => {
                                assignedMasters.reload();
                                employeeTasks.reload();
                              }}
                            />
                          </td>
                          <td>
                            {canManageTaskAssignments && m.is_recurring && (
                              <button
                                className="btn btn-ghost btn-sm"
                                onClick={() => setEditingAttachment(m)}
                                title="Edit priority and automatic billing"
                              >
                                <Icon name="edit" size={13} /> Edit
                              </button>
                            )}
                            {(canManageTaskAssignments || (canAttachOneOff && !m.is_recurring)) && (
                              <button
                                className="btn btn-ghost btn-sm"
                                onClick={async () => {
                                  if (!window.confirm("Detach this task master from the business?")) return;
                                  try {
                                    await removeBusinessTask(id, m.id);
                                    showToast("success", "Task detached");
                                    assignedMasters.reload();
                                    employeeTasks.reload();
                                  } catch (err) {
                                    showApiError(err);
                                  }
                                }}
                              >
                                <Icon name="trash" size={13} /> Detach
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}

          {tab === "drive" && (
            <BusinessDrivePanel businessId={b.id} businessName={b.business_name} />
          )}

          {tab === "activity" && role === "admin" && (
            <ActivityTab businessId={b.id} />
          )}

          {tab === "payments" && (
            <BusinessPaymentsPanel businessId={b.id} onCountChange={setPaymentCount} />
          )}

          {tab === "notes" && (
            <NotesTab
              loading={notesApi.loading}
              notes={noteRows}
              currentUserId={user?.id}
              isAdmin={role === "admin"}
              onCreate={async (note) => { await createBusinessNote(id, { note }); notesApi.reload(); }}
              onUpdate={async (noteId, note) => { await updateBusinessNote(noteId, { note }); notesApi.reload(); }}
              onDelete={async (noteId) => { await deleteBusinessNote(noteId); notesApi.reload(); }}
            />
          )}

          {tab === "instances" && (
            <div className="card">
              <div className="card-body no-pad">
                {employeeTasks.loading ? (
                  <div style={{ padding: 16 }}><LoadingBlock rows={4} /></div>
                ) : taskRows.length === 0 ? (
                  <EmptyState
                    icon="layers"
                    title="No task instances yet"
                    hint="Run the Ops generator after attaching task masters."
                  />
                ) : (
                  <table className="table">
                    <thead>
                      <tr>
                        <th style={{ width: 50 }} className="t-num">#</th>
                        <th>Task</th>
                        <th>Assignee</th>
                        <th>Status</th>
                        <th className="t-num">Due</th>
                        <th style={{ width: 70 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {taskRows.map((t, i) => (
                        <tr key={t.id} className="clickable" onClick={() => setOpenTask(t.id)}>
                          <td className="t-num t-muted">{serialNumber(null, i)}</td>
                          <td className="t-primary">{t.task_master?.name}</td>
                          <td>{t.employee?.name || <span className="badge badge-warning">Unassigned</span>}</td>
                          <td><StatusBadge status={t.status} /></td>
                          <td className="t-num t-muted">{formatDateShort(t.due_date)}</td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <button
                              className="icon-btn"
                              title="View"
                              onClick={() => setOpenTask(t.id)}
                            >
                              <Icon name="eye" size={13} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="col gap-md">
          <div className="card">
            <div className="card-head"><div className="card-title">At a glance</div></div>
            <div className="card-body" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Stat label="Field groups" value={b.field_groups_count ?? groupRows.length} />
              <Stat label="Task masters" value={b.task_masters_count ?? assignedRows.length} />
            </div>
          </div>
          <div className="card">
            <div className="card-head"><div className="card-title">Contact</div></div>
            <div className="card-body col gap-sm">
              <div className="row gap-sm" title="MD Mobile">
                <Icon name="phone" size={14} style={{ color: "var(--ink-500)" }} />
                <span style={{ flex: 1 }}>{b.business_phone || "—"}</span>
              </div>
              <div className="row gap-sm">
                <Icon name="mail" size={14} style={{ color: "var(--ink-500)" }} />
                <span style={{ flex: 1, fontSize: 12, wordBreak: "break-all" }}>{b.business_email || "—"}</span>
              </div>
              {(b.concerned_person_name || b.concerned_person_phone) && (
                <div className="row gap-sm" title="Concerned person">
                  <Icon name="user" size={14} style={{ color: "var(--ink-500)" }} />
                  <span style={{ flex: 1, fontSize: 12 }}>
                    {b.concerned_person_name || "—"}
                    {b.concerned_person_phone && <span className="t-muted"> · {b.concerned_person_phone}</span>}
                  </span>
                </div>
              )}
              {b.address && (
                <div className="row gap-sm" style={{ alignItems: "flex-start" }} title="Address">
                  <Icon name="home" size={14} style={{ color: "var(--ink-500)", marginTop: 2 }} />
                  <span style={{ flex: 1, fontSize: 12, whiteSpace: "pre-wrap" }}>{b.address}</span>
                </div>
              )}
              {b.client && (
                <div className="row gap-sm">
                  <Icon name="user" size={14} style={{ color: "var(--ink-500)" }} />
                  <span style={{ flex: 1 }}>Client: {b.client.name}</span>
                </div>
              )}
            </div>
          </div>
          {b.description && (
            <div className="card">
              <div className="card-head"><div className="card-title">Description</div></div>
              <div className="card-body" style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{b.description}</div>
            </div>
          )}
        </div>
      </div>

      <CreateFieldGroupDrawer
        open={openCreateGroup}
        onClose={() => setOpenCreateGroup(false)}
        businessId={id}
        onCreated={() => {
          setOpenCreateGroup(false);
          fieldGroups.reload();
        }}
      />

      <ImportFieldGroupsDrawer
        open={openImportGroups}
        onClose={() => setOpenImportGroups(false)}
        businessId={id}
        onImported={() => {
          setOpenImportGroups(false);
          fieldGroups.reload();
        }}
      />

      <AssignTaskMasterDrawer
        open={openAssignTask}
        onClose={() => setOpenAssignTask(false)}
        businessId={id}
        alreadyAttached={assignedRows.map((m) => m.id)}
        oneOffOnly={!canManageTaskAssignments}
        onAssigned={() => {
          // Keep the drawer open so admins can attach several templates in a
          // row; the row flips to "Attached" via the refreshed parent state.
          assignedMasters.reload();
          // A one-off attach also spins up its instance — pull it into the
          // Instances tab right away.
          employeeTasks.reload();
        }}
      />

      {/* Same drawer as attach, in edit mode — so the billing form an admin
          filled in on attach is the one they come back to. */}
      <RecurringTaskAttachDrawer
        master={editingAttachment}
        mode="edit"
        initial={editingAttachment?.pivot}
        // Verified but not yet billed — exactly what an untick stands down.
        scheduledCount={
          taskRows.filter(
            (t) =>
              t.task_master_id === editingAttachment?.id &&
              t.auto_invoice &&
              t.invoice_due_on &&
              !t.auto_invoice_generated_at,
          ).length
        }
        saving={savingAttachment}
        onClose={() => setEditingAttachment(null)}
        onSubmit={async (payload) => {
          setSavingAttachment(true);
          try {
            const res = await updateBusinessTask(id, editingAttachment.id, payload);
            const affected = res?.data?.scheduled_invoices_affected ?? 0;
            showToast(
              "success",
              affected > 0
                ? `Saved · ${affected} scheduled invoice${affected === 1 ? "" : "s"} updated`
                : res?.message || "Saved",
            );
            setEditingAttachment(null);
            assignedMasters.reload();
            employeeTasks.reload();
          } catch (err) {
            showApiError(err);
          } finally {
            setSavingAttachment(false);
          }
        }}
      />

      <TaskDrawer
        taskId={openTask}
        role="admin"
        onClose={() => setOpenTask(null)}
        onChanged={() => employeeTasks.reload()}
      />

      <LoginDrawer
        mode={loginMode}
        businessName={b.business_name}
        businessId={b.id}
        phone={b.business_phone}
        onClose={() => setLoginMode(null)}
        onDone={() => {
          setLoginMode(null);
          biz.reload();
        }}
      />
    </>
  );
}

// The auto-username the backend derives from a phone: its local 10 digits
// (country code dropped). We prefill the create form with this so the admin
// sees the default and can tweak it; clearing the field falls back to the same
// server-side generation (which also handles collisions). Mirrors
// App\Support\Credentials::uniqueUsernameFromPhone.
function usernameFromPhone(phone) {
  const digits = String(phone || "").replace(/\D/g, "");
  return digits.length > 10 ? digits.slice(-10) : digits;
}

// Grant a customer login or reset its password. Username (create only) and
// password are optional — left blank, each auto-generates (temp password is
// shown once via the credentials dialog); set explicitly, they're used as-is.
function LoginDrawer({ mode, businessName, businessId, phone, onClose, onDone }) {
  const open = mode === "create" || mode === "reset";
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [saving, setSaving] = useState(false);

  // (Re)seed the fields each time the drawer opens: prefill the username from
  // the phone on create, password always starts blank (auto temp).
  useEffect(() => {
    if (open) {
      setUsername(mode === "create" ? usernameFromPhone(phone) : "");
      setPassword("");
    }
  }, [open, mode, phone]);

  const submit = async () => {
    setSaving(true);
    try {
      let res;
      if (mode === "create") {
        const payload = {};
        if (username.trim()) payload.username = username.trim();
        if (password) payload.password = password;
        res = await createBusinessLogin(businessId, payload);
      } else {
        res = await resetBusinessLoginPassword(businessId, password ? { password } : {});
      }
      // showCredentials returns false when no temp password was generated
      // (i.e. the admin set an explicit one) — fall back to a success toast.
      if (!showCredentials(res)) {
        showToast("success", res.message || (mode === "create" ? "Login created" : "Password reset"));
      }
      onDone();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={mode === "create" ? "Grant customer login" : "Reset portal password"}
      subtitle={businessName}
      footer={
        <div className="row gap-sm" style={{ justifyContent: "flex-end" }}>
          <button className="btn btn-ghost" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving}>
            {saving
              ? (mode === "create" ? "Creating…" : "Resetting…")
              : (mode === "create" ? "Create login" : "Reset password")}
          </button>
        </div>
      }
    >
      <div style={{ display: "grid", gap: 14 }}>
        {mode === "create" && (
          <div className="field">
            <label className="field-label">Username <span className="t-muted">(optional)</span></label>
            <input
              className="input"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Auto from phone number"
              autoComplete="off"
            />
            <div className="t-muted text-xs" style={{ marginTop: 4 }}>
              Prefilled from the phone — edit it, or clear to auto-generate. Lowercase letters, numbers and . _ - only (min 3).
            </div>
          </div>
        )}
        <div className="field">
          <label className="field-label">Password <span className="t-muted">(optional)</span></label>
          <input
            className="input"
            type="text"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Auto-generate temp password"
            autoComplete="off"
          />
          <div className="t-muted text-xs" style={{ marginTop: 4 }}>
            Blank → a one-time temp password the client must change on first login. Set one (min 8) to use as-is.
          </div>
        </div>
        {mode === "reset" && (
          <div className="t-muted text-xs">The client will be signed out of all devices.</div>
        )}
      </div>
    </Drawer>
  );
}

function NotesTab({ loading, notes, currentUserId, isAdmin, onCreate, onUpdate, onDelete }) {
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState("");
  const [busyId, setBusyId] = useState(null);

  const canManage = (n) => isAdmin || (currentUserId != null && n.created_by === currentUserId);

  const submit = async () => {
    const text = draft.trim();
    if (!text) return;
    setSaving(true);
    try {
      await onCreate(text);
      setDraft("");
      showToast("success", "Note added");
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  const saveEdit = async (id) => {
    const text = editText.trim();
    if (!text) return;
    setBusyId(id);
    try {
      await onUpdate(id, text);
      setEditId(null);
      setEditText("");
      showToast("success", "Note updated");
    } catch (err) {
      showApiError(err);
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this note? This cannot be undone.")) return;
    setBusyId(id);
    try {
      await onDelete(id);
      showToast("success", "Note deleted");
    } catch (err) {
      showApiError(err);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="card">
      <div className="card-body col gap-md">
        <div>
          <textarea
            className="textarea"
            rows={3}
            placeholder="Add a note about this client…"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
            <button className="btn btn-primary btn-sm" onClick={submit} disabled={saving || !draft.trim()}>
              <Icon name="plus" size={13} /> {saving ? "Adding…" : "Add note"}
            </button>
          </div>
        </div>

        {loading ? (
          <LoadingBlock rows={3} />
        ) : notes.length === 0 ? (
          <EmptyState icon="file-text" title="No notes yet" hint="Add the first note about this client." />
        ) : (
          <div className="col gap-sm">
            {notes.map((n) => (
              <div key={n.id} className="note">
                <div className="note-meta" style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span className="note-author">{n.author?.name || "Unknown"}</span>
                  <span>·</span>
                  <span>{formatDateTime(n.created_at)}</span>
                  {n.updated_at && n.updated_at !== n.created_at && (
                    <span className="t-muted">· edited</span>
                  )}
                  {canManage(n) && editId !== n.id && (
                    <span style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
                      <button
                        className="icon-btn"
                        title="Edit"
                        onClick={() => { setEditId(n.id); setEditText(n.note); }}
                        disabled={busyId === n.id}
                      >
                        <Icon name="edit" size={13} />
                      </button>
                      <button
                        className="icon-btn"
                        title="Delete"
                        style={{ color: "var(--danger)" }}
                        onClick={() => remove(n.id)}
                        disabled={busyId === n.id}
                      >
                        <Icon name="trash" size={13} />
                      </button>
                    </span>
                  )}
                </div>
                {editId === n.id ? (
                  <div style={{ marginTop: 6 }}>
                    <textarea
                      className="textarea"
                      rows={3}
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                    />
                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, marginTop: 6 }}>
                      <button className="btn btn-ghost btn-sm" onClick={() => { setEditId(null); setEditText(""); }} disabled={busyId === n.id}>
                        Cancel
                      </button>
                      <button className="btn btn-primary btn-sm" onClick={() => saveEdit(n.id)} disabled={busyId === n.id || !editText.trim()}>
                        {busyId === n.id ? "Saving…" : "Save"}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="note-body" style={{ whiteSpace: "pre-wrap" }}>{n.note}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FieldGroupCard({ group, open, onToggle, revealed, setRevealed, onChanged, canAdd = false, canEdit = false, canDelete = false }) {
  const fields = useApi(() => listFields(group.id, { per_page: 100 }), [group.id]);
  const fieldRows = paginatedRows(fields.data);
  const [fieldDrawer, setFieldDrawer] = useState(null); // null | { mode: 'create' } | { mode: 'edit', field }
  const [groupEditOpen, setGroupEditOpen] = useState(false);
  const [justCopied, setJustCopied] = useState(null); // field id, transient

  const copyField = async (f) => {
    const value = f.value || "";
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setJustCopied(f.id);
      showToast("success", `${f.field_name} copied`);
      setTimeout(() => setJustCopied((cur) => (cur === f.id ? null : cur)), 1200);
    } catch {
      showToast("error", "Couldn't copy to clipboard");
    }
  };

  const handleDeleteGroup = async () => {
    const fieldCount = group.fields_count ?? fieldRows.length;
    const note = fieldCount > 0
      ? `\n\nThis group has ${fieldCount} field${fieldCount === 1 ? "" : "s"}. The backend will refuse if any are present — delete them first.`
      : "";
    if (!window.confirm(`Delete field group "${group.name}"?${note}`)) return;
    try {
      await deleteFieldGroup(group.id);
      showToast("success", "Field group deleted");
      onChanged?.();
    } catch (err) {
      showApiError(err);
    }
  };

  const handleDeleteField = async (f) => {
    if (!window.confirm(`Delete field "${f.field_name}"?`)) return;
    try {
      await deleteField(f.id);
      showToast("success", "Field deleted");
      fields.reload();
      onChanged?.();
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <div className="card">
      <div className="card-head" style={{ cursor: "pointer" }} onClick={onToggle}>
        <div className="row gap-sm" style={{ flex: 1, minWidth: 0 }}>
          <Icon name={open ? "chevron-down" : "chevron-right"} size={14} style={{ color: "var(--ink-500)" }} />
          <div style={{ minWidth: 0 }}>
            <div className="card-title" style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {group.name}
            </div>
            <div className="card-sub">{group.fields_count ?? fieldRows.length} fields</div>
          </div>
        </div>
        <div className="row gap-sm" onClick={(e) => e.stopPropagation()}>
          {canAdd && (
            <button
              className="btn btn-ghost btn-sm"
              onClick={() => setFieldDrawer({ mode: "create" })}
            >
              <Icon name="plus" size={12} /> Add field
            </button>
          )}
          {canEdit && (
            <button
              className="icon-btn"
              title="Edit group"
              onClick={() => setGroupEditOpen(true)}
            >
              <Icon name="edit" size={13} />
            </button>
          )}
          {canDelete && (
            <button
              className="icon-btn"
              title="Delete group"
              onClick={handleDeleteGroup}
              style={{ color: "var(--danger)" }}
            >
              <Icon name="trash" size={13} />
            </button>
          )}
        </div>
      </div>
      {open && (
        <div className="card-body" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {fields.loading ? (
            <LoadingBlock rows={2} height={48} />
          ) : fieldRows.length === 0 ? (
            <div style={{ color: "var(--ink-500)", fontSize: 13, gridColumn: "1 / -1" }}>No fields yet.</div>
          ) : (
            fieldRows.map((f) => {
              const isPwd = f.field_type === "password";
              const isLink = f.field_type === "link";
              const isRevealed = revealed[f.id];
              const copied = justCopied === f.id;
              // Reserve room on the right for the copy button (+ the eye toggle
              // on passwords, which sits in the rightmost slot).
              const inputRightPad = isPwd ? 68 : 36;
              return (
                <div key={f.id} className="field">
                  <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
                    <div className="field-label" style={{ flex: 1, minWidth: 0 }}>
                      {f.field_name}
                      {f.is_required && <span style={{ color: "var(--danger)" }}> *</span>}
                    </div>
                    <div className="row gap-sm" style={{ alignItems: "center" }}>
                      <span className="text-xs t-muted">{f.field_type}</span>
                      {canEdit && (
                        <button
                          type="button"
                          className="icon-btn"
                          title="Edit field"
                          style={{ width: 24, height: 24 }}
                          onClick={() => setFieldDrawer({ mode: "edit", field: f })}
                        >
                          <Icon name="edit" size={12} />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          type="button"
                          className="icon-btn"
                          title="Delete field"
                          style={{ width: 24, height: 24, color: "var(--danger)" }}
                          onClick={() => handleDeleteField(f)}
                        >
                          <Icon name="trash" size={12} />
                        </button>
                      )}
                    </div>
                  </div>
                  <div style={{ position: "relative" }}>
                    {isLink && f.value ? (
                      <a
                        className="input"
                        href={normalizeUrl(f.value)}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={f.value}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          paddingRight: inputRightPad,
                          color: "var(--primary-700)",
                          textDecoration: "none",
                          overflow: "hidden",
                        }}
                      >
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            textDecoration: "underline",
                          }}
                        >
                          {f.value}
                        </span>
                      </a>
                    ) : (
                      <input
                        className="input"
                        value={isPwd ? (isRevealed ? f.value || "" : "••••••••") : f.value || ""}
                        readOnly
                        style={{
                          paddingRight: inputRightPad,
                          fontFamily: isPwd && isRevealed ? "var(--font-mono)" : "inherit",
                        }}
                      />
                    )}
                    <button
                      type="button"
                      className="icon-btn"
                      title={copied ? "Copied" : "Copy"}
                      style={{ position: "absolute", right: isPwd ? 36 : 4, top: 3, width: 28, height: 28, color: copied ? "var(--success)" : undefined }}
                      onClick={() => copyField(f)}
                      disabled={!f.value}
                    >
                      <Icon name={copied ? "check" : "copy"} size={14} />
                    </button>
                    {isPwd && (
                      <button
                        type="button"
                        className="icon-btn"
                        title={isRevealed ? "Hide password" : "Show password"}
                        style={{ position: "absolute", right: 4, top: 3, width: 28, height: 28 }}
                        onClick={() => setRevealed({ ...revealed, [f.id]: !isRevealed })}
                      >
                        <Icon name={isRevealed ? "eye-off" : "eye"} size={14} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      <FieldGroupDrawer
        open={groupEditOpen}
        mode="edit"
        group={group}
        onClose={() => setGroupEditOpen(false)}
        onSaved={() => {
          setGroupEditOpen(false);
          onChanged?.();
        }}
      />

      <FieldDrawer
        open={!!fieldDrawer}
        mode={fieldDrawer?.mode}
        field={fieldDrawer?.field}
        fieldGroupId={group.id}
        onClose={() => setFieldDrawer(null)}
        onSaved={() => {
          setFieldDrawer(null);
          fields.reload();
          onChanged?.();
        }}
      />
    </div>
  );
}

function FieldGroupDrawer({ open, mode = "create", group, onClose, businessId, onSaved }) {
  const [name, setName] = useState("");
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setName(mode === "edit" && group ? group.name : "");
    setErrors({});
  }, [open, mode, group]);

  const submit = async () => {
    setErrors({});
    setSaving(true);
    try {
      if (mode === "edit") {
        await updateFieldGroup(group.id, { name });
        showToast("success", "Field group updated");
      } else {
        await createBusinessFieldGroup(businessId, { name });
        showToast("success", "Field group created");
      }
      onSaved?.();
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={mode === "edit" ? `Edit ${group?.name || "field group"}` : "New field group"}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || !name}>
            {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Create"}
          </button>
        </>
      }
    >
      <div className="field">
        <label className="field-label">Name</label>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="GST credentials · Income tax · Bank" />
        {errors.name?.[0] && <div className="field-error">{errors.name[0]}</div>}
      </div>
    </Drawer>
  );
}

// Backward-compat alias used at the page level for the "Add field group" button.
const CreateFieldGroupDrawer = ({ open, onClose, businessId, onCreated }) => (
  <FieldGroupDrawer
    open={open}
    mode="create"
    onClose={onClose}
    businessId={businessId}
    onSaved={onCreated}
  />
);

function FieldDrawer({ open, mode = "create", field, fieldGroupId, onClose, onSaved }) {
  const [form, setForm] = useState({
    field_name: "",
    field_type: "string",
    is_required: false,
    value: "",
    order: 0,
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (mode === "edit" && field) {
      setForm({
        field_name: field.field_name || "",
        field_type: field.field_type || "string",
        is_required: !!field.is_required,
        value: field.value ?? "",
        order: field.order ?? 0,
      });
    } else {
      setForm({ field_name: "", field_type: "string", is_required: false, value: "", order: 0 });
    }
    setErrors({});
    setShowPwd(false);
  }, [open, mode, field]);

  const submit = async () => {
    setErrors({});
    setSaving(true);
    try {
      if (mode === "edit") {
        await updateField(field.id, form);
        showToast("success", "Field updated");
      } else {
        await createField(fieldGroupId, form);
        showToast("success", "Field created");
      }
      onSaved?.();
    } catch (err) {
      if (err?.data?.errors) setErrors(err.data.errors);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={mode === "edit" ? `Edit ${field?.field_name || "field"}` : "New field"}
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={saving || !form.field_name}>
            {saving ? "Saving…" : mode === "edit" ? "Save changes" : "Create"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <div className="field">
          <label className="field-label">Name</label>
          <input className="input" value={form.field_name} onChange={(e) => setForm({ ...form, field_name: e.target.value })} />
          {errors.field_name?.[0] && <div className="field-error">{errors.field_name[0]}</div>}
        </div>
        <div className="field">
          <label className="field-label">Type</label>
          <select className="select" value={form.field_type} onChange={(e) => setForm({ ...form, field_type: e.target.value })}>
            <option value="string">Text (single line)</option>
            <option value="text">Text (multi-line)</option>
            <option value="integer">Integer</option>
            <option value="boolean">Boolean</option>
            <option value="date">Date</option>
            <option value="password">Password</option>
            <option value="link">Link (URL)</option>
          </select>
        </div>
        <div className="field">
          <label className="field-label">Value</label>
          {form.field_type === "text" ? (
            <textarea className="textarea" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
          ) : form.field_type === "password" ? (
            <div style={{ position: "relative" }}>
              <input
                className="input"
                type={showPwd ? "text" : "password"}
                value={form.value}
                onChange={(e) => setForm({ ...form, value: e.target.value })}
                style={{ paddingRight: 36, fontFamily: showPwd ? "var(--font-mono)" : "inherit" }}
              />
              <button
                type="button"
                className="icon-btn"
                title={showPwd ? "Hide" : "Show"}
                style={{ position: "absolute", right: 4, top: 3, width: 28, height: 28 }}
                onClick={() => setShowPwd((s) => !s)}
              >
                <Icon name={showPwd ? "eye-off" : "eye"} size={14} />
              </button>
            </div>
          ) : (
            <input
              className="input"
              type="text"
              value={form.value}
              onChange={(e) => setForm({ ...form, value: e.target.value })}
            />
          )}
        </div>
        <div className="row gap-sm">
          <input
            type="checkbox"
            id="req"
            checked={form.is_required}
            onChange={(e) => setForm({ ...form, is_required: e.target.checked })}
          />
          <label htmlFor="req" className="field-label" style={{ marginBottom: 0 }}>Required field</label>
        </div>
      </div>
    </Drawer>
  );
}

function AssignTaskMasterDrawer({ open, onClose, businessId, alreadyAttached, oneOffOnly = false, onAssigned }) {
  const masters = useApi(() => listTaskMasters({ per_page: 200 }), [], { immediate: false });
  const rows = paginatedRows(masters.data);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [groupFilter, setGroupFilter] = useState(""); // "" = all groups
  const [oneOffMaster, setOneOffMaster] = useState(null); // one-off template being configured
  const [recurringMaster, setRecurringMaster] = useState(null); // recurring template — pick priority

  useEffect(() => {
    if (open) {
      setSearch("");
      setGroupFilter("");
      // Surface load failures (e.g. a 403 from a missing permission) so the
      // user can see why the picker is empty instead of just blank state.
      masters.reload().catch((err) => showApiError(err));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Group dropdown options derived from the loaded templates.
  const groupOptions = useMemo(() => {
    const map = new Map();
    rows.forEach((m) => {
      if (m.task_group && !map.has(m.task_group.id)) map.set(m.task_group.id, m.task_group.name);
    });
    return Array.from(map, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [rows]);
  // Same list in SearchableSelect's {id,label} shape.
  const groupSelectOptions = useMemo(
    () => groupOptions.map((g) => ({ id: g.id, label: g.name })),
    [groupOptions],
  );

  // Filter (group + search) then bucket by task group; ungrouped sinks last.
  const grouped = useMemo(() => {
    const term = search.trim().toLowerCase();
    const buckets = new Map(); // key -> { name, items }
    rows.forEach((m) => {
      if (oneOffOnly && m.is_recurring) return;
      if (groupFilter && String(m.task_group?.id ?? "") !== String(groupFilter)) return;
      if (term && !(m.name || "").toLowerCase().includes(term)) return;
      const key = m.task_group?.id ?? "__none";
      if (!buckets.has(key)) buckets.set(key, { name: m.task_group?.name || "Ungrouped", items: [] });
      buckets.get(key).items.push(m);
    });
    return Array.from(buckets, ([key, val]) => ({ key, ...val })).sort((a, b) => {
      if (a.key === "__none") return 1;
      if (b.key === "__none") return -1;
      return a.name.localeCompare(b.name);
    });
  }, [rows, search, groupFilter, oneOffOnly]);

  // Generic attach. Recurring rows attach directly; one-off rows pass the
  // dialog payload (due date, priority, optional ad-hoc payment + receipt).
  const attach = async (taskMasterId, extra = {}) => {
    setSaving(true);
    try {
      const res = await assignBusinessTask(businessId, { task_master_id: taskMasterId, ...extra });
      showToast("success", res?.message || "Task master attached");
      onAssigned?.();
      return true;
    } catch (err) {
      showApiError(err);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const renderRow = (m) => {
    const attached = alreadyAttached.includes(m.id);
    return (
      <tr key={m.id}>
        <td className="t-primary">{m.name}</td>
        <td className="t-muted">{m.is_recurring ? (m.recurrence_pattern || "—") : "One-off"}</td>
        <td>
          {attached ? (
            <span className="badge badge-success">Attached</span>
          ) : m.is_recurring ? (
            <button className="btn btn-primary btn-sm" onClick={() => setRecurringMaster(m)} disabled={saving}>
              Attach…
            </button>
          ) : (
            <button className="btn btn-primary btn-sm" onClick={() => setOneOffMaster(m)} disabled={saving}>
              Attach…
            </button>
          )}
        </td>
      </tr>
    );
  };

  return (
    <>
    <Drawer
      open={open}
      onClose={onClose}
      title="Assign task master"
      subtitle={oneOffOnly ? "Pick a one-off template to attach (you'll be the assignee)" : "Pick a template to attach to this business"}
      wide
    >
      {masters.loading ? (
        <LoadingBlock rows={5} />
      ) : rows.length === 0 ? (
        <EmptyState icon="layers" title="No task masters" />
      ) : (
        <div className="col gap-md">
          <div className="row gap-sm" style={{ alignItems: "center" }}>
            {/* The search box beside this filters the task-master list below,
                not this dropdown's options — the two are complementary. */}
            <SearchableSelect
              value={groupFilter}
              onChange={(v) => setGroupFilter(v ?? "")}
              options={groupSelectOptions}
              placeholder="All groups"
              searchPlaceholder="Search groups…"
              width={200}
            />
            <div style={{ position: "relative", flex: 1 }}>
              <Icon
                name="search"
                size={13}
                style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-400)", pointerEvents: "none" }}
              />
              <input
                className="input"
                style={{ paddingLeft: 30 }}
                placeholder="Search task masters…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>

          {grouped.length === 0 ? (
            <EmptyState icon="search" title="No task masters match" hint="Try a different search or group." />
          ) : (
            grouped.map((grp) => (
              <div key={grp.key}>
                <div className="row" style={{ justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <div className="t-primary fw-600">{grp.name}</div>
                  <span className="text-xs t-muted">{grp.items.length} task{grp.items.length === 1 ? "" : "s"}</span>
                </div>
                <table className="table">
                  <thead><tr><th>Task</th><th>Recurrence</th><th></th></tr></thead>
                  <tbody>{grp.items.map((m) => renderRow(m))}</tbody>
                </table>
              </div>
            ))
          )}
        </div>
      )}
    </Drawer>

    <OneOffTaskAttachDrawer
      master={oneOffMaster}
      saving={saving}
      onClose={() => setOneOffMaster(null)}
      onSubmit={async (payload) => {
        const ok = await attach(oneOffMaster.id, payload);
        if (ok) setOneOffMaster(null);
      }}
    />

    <RecurringTaskAttachDrawer
      master={recurringMaster}
      saving={saving}
      onClose={() => setRecurringMaster(null)}
      onSubmit={async (payload) => {
        const ok = await attach(recurringMaster.id, payload);
        if (ok) setRecurringMaster(null);
      }}
    />
    </>
  );
}

// today + N days as a YYYY-MM-DD string (local date).
function addDaysISO(days) {
  const d = new Date();
  d.setDate(d.getDate() + Number(days || 0));
  const tzOffset = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - tzOffset).toISOString().slice(0, 10);
}

// Config dialog shown when attaching a ONE-OFF task: due date + priority, and
// an optional ad-hoc payment (amount, GST, due date) with an optional receipt.
function OneOffTaskAttachDrawer({ master, saving, onClose, onSubmit }) {
  const open = !!master;
  const [dueDate, setDueDate] = useState("");
  const [priority, setPriority] = useState("medium");
  const [createPayment, setCreatePayment] = useState(false);
  const [amount, setAmount] = useState("");
  const [gst, setGst] = useState("no_gst");
  const [payDue, setPayDue] = useState("");
  const [recordReceipt, setRecordReceipt] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const [receipt, setReceipt] = useState({ amount: "", received_on: today, method: "bank_transfer", reference: "", notes: "" });

  useEffect(() => {
    if (!master) return;
    // One-off task masters carry a lead time in days; the due date defaults to
    // today + that many days. With no lead time set, the admin picks a date.
    const days = master.non_recurring_due_in_days;
    const due = days == null ? "" : addDaysISO(days);
    setDueDate(due);
    setPriority("medium");
    setCreatePayment(false);
    setAmount("");
    setGst("no_gst");
    setPayDue(due);
    setRecordReceipt(false);
    setReceipt({ amount: "", received_on: today, method: "bank_transfer", reference: "", notes: "" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [master]);

  // Changing the task due date pulls the payment due date along with it (the
  // bill is for this task, so they default in lockstep). Editing the payment
  // due date on its own does NOT push back onto the task — one-way only.
  const onChangeDueDate = (value) => {
    setDueDate(value);
    setPayDue(value);
  };

  const paymentValid = !createPayment || (amount !== "" && Number(amount) >= 0 && gst && payDue);
  const receiptValid = !recordReceipt || (receipt.amount !== "" && Number(receipt.amount) > 0 && receipt.received_on && receipt.method);
  const canSubmit = !!dueDate && paymentValid && receiptValid && !saving;

  const submit = () => {
    const payload = { priority };
    if (dueDate) payload.due_date = dueDate;
    if (createPayment) {
      payload.create_payment = true;
      payload.payment_amount = Number(amount);
      payload.payment_gst_treatment = gst;
      payload.payment_due_date = payDue;
      if (recordReceipt && receipt.amount !== "") {
        payload.receipt = {
          amount: Number(receipt.amount),
          received_on: receipt.received_on,
          method: receipt.method,
          reference: receipt.reference || null,
          notes: receipt.notes || null,
        };
      }
    }
    onSubmit?.(payload);
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={master ? `Attach: ${master.name}` : "Attach one-off task"}
      subtitle="One-off task — set the due date, priority, and an optional payment."
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={!canSubmit}>
            {saving ? "Attaching…" : "Attach"}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <div className="field">
          <label className="field-label">Task due date</label>
          <input type="date" className="input" value={dueDate} onChange={(e) => onChangeDueDate(e.target.value)} />
          <div className="field-hint">When the task itself is due. Defaults to today + the template's lead days; the payment due date follows it.</div>
        </div>

        <div className="field">
          <label className="field-label">Priority</label>
          <div className="row gap-sm">
            {[["low", "Low"], ["medium", "Medium"], ["high", "High"]].map(([k, label]) => (
              <button
                key={k}
                type="button"
                className={`btn btn-sm ${priority === k ? "btn-primary" : "btn-secondary"}`}
                onClick={() => setPriority(k)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        <label className="row gap-sm" style={{ alignItems: "center", cursor: "pointer" }}>
          <input type="checkbox" checked={createPayment} onChange={(e) => setCreatePayment(e.target.checked)} />
          <span className="text-sm fw-600">Create a payment for this task</span>
        </label>

        {createPayment && (
          <div className="card" style={{ background: "var(--surface-2)" }}>
            <div className="card-body col gap-md">
              <div className="row gap-md" style={{ flexWrap: "wrap" }}>
                <div className="field" style={{ flex: 1, minWidth: 160 }}>
                  <label className="field-label">Amount (₹)</label>
                  <input type="number" step="0.01" min="0" className="input" value={amount}
                    onChange={(e) => setAmount(e.target.value)} placeholder="5000.00" />
                </div>
                <div className="field" style={{ flex: 1, minWidth: 160 }}>
                  <label className="field-label">Payment due date</label>
                  <input type="date" className="input" value={payDue} onChange={(e) => setPayDue(e.target.value)} />
                </div>
              </div>

              <div className="field">
                <label className="field-label">GST treatment</label>
                <div className="row gap-sm">
                  {[["gst", "GST"], ["no_gst", "No GST"]].map(([k, label]) => (
                    <button key={k} type="button"
                      className={`btn btn-sm ${gst === k ? "btn-primary" : "btn-secondary"}`}
                      onClick={() => setGst(k)}>
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              <label className="row gap-sm" style={{ alignItems: "center", cursor: "pointer" }}>
                <input type="checkbox" checked={recordReceipt} onChange={(e) => setRecordReceipt(e.target.checked)} />
                <span className="text-sm fw-600">Record a receipt now</span>
              </label>

              {recordReceipt && (
                <div className="col gap-md">
                  <div className="row gap-md" style={{ flexWrap: "wrap" }}>
                    <div className="field" style={{ flex: 1, minWidth: 140 }}>
                      <label className="field-label">Amount (₹)</label>
                      <input type="number" step="0.01" min="0.01" className="input" value={receipt.amount}
                        onChange={(e) => setReceipt((r) => ({ ...r, amount: e.target.value }))} />
                    </div>
                    <div className="field" style={{ flex: 1, minWidth: 140 }}>
                      <label className="field-label">Received on</label>
                      <input type="date" className="input" value={receipt.received_on}
                        onChange={(e) => setReceipt((r) => ({ ...r, received_on: e.target.value }))} />
                    </div>
                    <div className="field" style={{ flex: 1, minWidth: 140 }}>
                      <label className="field-label">Method</label>
                      <select className="select" value={receipt.method}
                        onChange={(e) => setReceipt((r) => ({ ...r, method: e.target.value }))}>
                        {Object.entries(RECEIPT_METHOD_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="field">
                    <label className="field-label">Reference <span className="t-muted text-xs">(optional)</span></label>
                    <input type="text" className="input" value={receipt.reference}
                      onChange={(e) => setReceipt((r) => ({ ...r, reference: e.target.value }))}
                      placeholder="Txn id · cheque number · UPI ref" />
                  </div>
                  <div className="field">
                    <label className="field-label">Notes <span className="t-muted text-xs">(optional)</span></label>
                    <textarea className="textarea" rows={2} value={receipt.notes}
                      onChange={(e) => setReceipt((r) => ({ ...r, notes: e.target.value }))} />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </Drawer>
  );
}

// Dialog for a RECURRING task — used both to attach one and to edit an
// existing attachment. Instances are materialised later by the scheduler, so
// there's no due date here; what IS set is the priority every generated
// instance inherits, and optionally auto-invoicing.
//
// Auto-invoicing bills the client automatically once an admin VERIFIES each
// completed instance: `delay_days` after that the bill + tax invoice are
// raised, and payment falls due `due_days` after that.
//
// `mode` is "attach" or "edit"; in edit mode `initial` prefills from the pivot.
function RecurringTaskAttachDrawer({ master, saving, onClose, onSubmit, mode = "attach", initial = null, scheduledCount = 0 }) {
  const open = !!master;
  const isEdit = mode === "edit";
  const [priority, setPriority] = useState("medium");
  const [autoInvoice, setAutoInvoice] = useState(false);
  const [firmProfiles, setFirmProfiles] = useState([]);
  const [inv, setInv] = useState({
    firm_profile_id: "",
    amount: "",
    gst: "no_gst",
    description: "",
    delay_days: "0",
    due_days: "15",
  });

  // Letterheads drive both the invoice's identity and its numbering series, so
  // one has to be pinned per attachment — there's no "default" firm profile.
  useEffect(() => {
    if (!open) return;
    listFirmProfiles()
      .then((res) => setFirmProfiles(res?.data || []))
      .catch(() => setFirmProfiles([]));
  }, [open]);

  useEffect(() => {
    if (!master) return;
    setPriority(initial?.priority || "medium");
    setAutoInvoice(!!initial?.auto_invoice);
    setInv({
      firm_profile_id: initial?.auto_invoice_firm_profile_id ? String(initial.auto_invoice_firm_profile_id) : "",
      amount: initial?.auto_invoice_amount != null ? String(initial.auto_invoice_amount) : "",
      gst: initial?.auto_invoice_gst_treatment || "no_gst",
      description: initial?.auto_invoice_description || "",
      delay_days: initial?.auto_invoice_delay_days != null ? String(initial.auto_invoice_delay_days) : "0",
      due_days: initial?.auto_invoice_due_days != null ? String(initial.auto_invoice_due_days) : "15",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [master]);

  // Single-firm setups shouldn't have to pick.
  useEffect(() => {
    if (firmProfiles.length === 1 && !inv.firm_profile_id) {
      setInv((s) => ({ ...s, firm_profile_id: String(firmProfiles[0].id) }));
    }
  }, [firmProfiles, inv.firm_profile_id]);

  const set = (k) => (e) => setInv((s) => ({ ...s, [k]: e.target.value }));

  const invoiceValid =
    !autoInvoice ||
    (inv.firm_profile_id &&
      inv.amount !== "" &&
      Number(inv.amount) >= 0 &&
      inv.description.trim() !== "" &&
      inv.delay_days !== "" &&
      inv.due_days !== "");
  const canSubmit = invoiceValid && !saving;

  // Turning billing off on an attachment that has invoices already scheduled
  // stands them down — worth confirming before it happens silently.
  // Boolean() is load-bearing: pivot columns aren't cast, so `auto_invoice`
  // arrives as 0/1, and a bare `0 &&` in JSX renders a stray "0".
  const willCancel = Boolean(isEdit && initial?.auto_invoice && !autoInvoice && scheduledCount > 0);

  const submit = () => {
    const payload = { priority };
    if (autoInvoice) {
      payload.auto_invoice = true;
      payload.auto_invoice_firm_profile_id = Number(inv.firm_profile_id);
      payload.auto_invoice_amount = Number(inv.amount);
      payload.auto_invoice_gst_treatment = inv.gst;
      payload.auto_invoice_description = inv.description.trim();
      payload.auto_invoice_delay_days = Number(inv.delay_days);
      payload.auto_invoice_due_days = Number(inv.due_days);
    } else if (isEdit) {
      // Only meaningful on edit — an attach simply omits the block.
      payload.auto_invoice = false;
    }
    onSubmit?.(payload);
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={master ? `${isEdit ? "Edit" : "Attach"}: ${master.name}` : "Attach recurring task"}
      subtitle="Recurring task — set the priority applied to every generated instance, and optional automatic billing."
      footer={
        <>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
          <button className="btn btn-primary" onClick={submit} disabled={!canSubmit}>
            {saving ? (isEdit ? "Saving…" : "Attaching…") : (isEdit ? "Save" : "Attach")}
          </button>
        </>
      }
    >
      <div className="col gap-md">
        <div className="field">
          <label className="field-label">Priority</label>
          <div className="row gap-sm">
            {[["low", "Low"], ["medium", "Medium"], ["high", "High"]].map(([k, label]) => (
              <button
                key={k}
                type="button"
                className={`btn btn-sm ${priority === k ? "btn-primary" : "btn-secondary"}`}
                onClick={() => setPriority(k)}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="field-hint">Carried onto every instance the scheduler creates for this task.</div>
        </div>

        <label className="row gap-sm" style={{ alignItems: "center", cursor: "pointer" }}>
          <input type="checkbox" checked={autoInvoice} onChange={(e) => setAutoInvoice(e.target.checked)} />
          <span className="text-sm fw-600">Create invoice after task completion</span>
        </label>

        {autoInvoice && (
          <div className="card" style={{ background: "var(--surface-2)" }}>
            <div className="card-body col gap-md">
              <div className="field">
                <label className="field-label">Bill from</label>
                <select className="select" value={inv.firm_profile_id} onChange={set("firm_profile_id")}>
                  <option value="">Select a firm profile…</option>
                  {firmProfiles.map((f) => (
                    <option key={f.id} value={f.id}>{f.firm_name}</option>
                  ))}
                </select>
                <div className="field-hint">
                  The letterhead printed on the invoice. Each firm profile keeps its own invoice-number series.
                </div>
              </div>

              <div className="row gap-md" style={{ flexWrap: "wrap" }}>
                <div className="field" style={{ flex: 1, minWidth: 160 }}>
                  <label className="field-label">Amount (₹)</label>
                  <input type="number" step="0.01" min="0" className="input" value={inv.amount}
                    onChange={set("amount")} placeholder="5000.00" />
                </div>
                <div className="field" style={{ flex: 1, minWidth: 160 }}>
                  <label className="field-label">GST treatment</label>
                  <div className="row gap-sm">
                    {[["gst", "GST"], ["no_gst", "No GST"]].map(([k, label]) => (
                      <button key={k} type="button"
                        className={`btn btn-sm ${inv.gst === k ? "btn-primary" : "btn-secondary"}`}
                        onClick={() => setInv((s) => ({ ...s, gst: k }))}>
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              {inv.gst === "gst" && (
                <div className="field-hint" style={{ marginTop: -6 }}>
                  The amount is GST-<strong>inclusive</strong> — tax is worked back out of it, so enter
                  what the client pays in total. CGST+SGST or IGST is chosen from the two states.
                </div>
              )}

              <div className="field">
                <label className="field-label">Item description</label>
                <input type="text" className="input" value={inv.description} onChange={set("description")}
                  placeholder="Monthly GST filing — retainer" />
                <div className="field-hint">Printed as the invoice line item.</div>
              </div>

              <div className="row gap-md" style={{ flexWrap: "wrap" }}>
                <div className="field" style={{ flex: 1, minWidth: 160 }}>
                  <label className="field-label">Create invoice after</label>
                  <div className="row gap-sm" style={{ alignItems: "center" }}>
                    <input type="number" min="0" max="365" className="input" style={{ width: 90 }}
                      value={inv.delay_days} onChange={set("delay_days")} />
                    <span className="text-sm t-muted">days</span>
                  </div>
                  <div className="field-hint">Counted from the day the completed task is verified. 0 = right away.</div>
                </div>
                <div className="field" style={{ flex: 1, minWidth: 160 }}>
                  <label className="field-label">Payment due after</label>
                  <div className="row gap-sm" style={{ alignItems: "center" }}>
                    <input type="number" min="0" max="365" className="input" style={{ width: 90 }}
                      value={inv.due_days} onChange={set("due_days")} />
                    <span className="text-sm t-muted">days</span>
                  </div>
                  <div className="field-hint">Counted from the invoice date.</div>
                </div>
              </div>

              <div className="field-hint">
                Nothing is billed until an admin verifies a completed instance of this task.
              </div>
            </div>
          </div>
        )}

        {willCancel && (
          <div className="field-hint" style={{ color: "var(--danger-600, #b42318)" }}>
            {scheduledCount} scheduled invoice{scheduledCount === 1 ? "" : "s"} will be cancelled.
            Invoices already issued are not affected.
          </div>
        )}
      </div>
    </Drawer>
  );
}

// Inline priority editor for an attached task. Persists to the pivot and
// cascades to the task's open instances (handled server-side). Updates
// optimistically and rolls back if the request fails.
function AttachedTaskPriorityCell({ businessId, taskMasterId, value, canEdit = true, onChanged }) {
  const [priority, setPriority] = useState(value || "medium");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPriority(value || "medium");
  }, [value]);

  const change = async (next) => {
    if (next === priority) return;
    const prev = priority;
    setPriority(next);
    setSaving(true);
    try {
      await updateBusinessTaskPriority(businessId, taskMasterId, next);
      showToast("success", "Priority updated");
      onChanged?.();
    } catch (err) {
      setPriority(prev);
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <select
      className="select"
      style={{ width: 110 }}
      value={priority}
      disabled={saving || !canEdit}
      title={canEdit ? undefined : "Admin only — you can only change one-off task priorities"}
      onChange={(e) => change(e.target.value)}
    >
      <option value="low">Low</option>
      <option value="medium">Medium</option>
      <option value="high">High</option>
    </select>
  );
}

const RECEIPT_METHOD_LABEL = {
  cash: "Cash",
  bank_transfer: "Bank transfer",
  upi: "UPI",
  cheque: "Cheque",
  card: "Card",
  other: "Other",
};

function Stat({ label, value }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--ink-500)", fontWeight: 600, letterSpacing: 0.4, textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: "var(--ink-900)", letterSpacing: -0.5, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function fmtActivityValue(v) {
  if (v === null || v === undefined || v === "") return "—";
  if (v === true) return "Yes";
  if (v === false) return "No";
  return String(v);
}

function prettyField(f) {
  return String(f).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

// Admin activity timeline for a client — newest first. Backed by the
// spatie/laravel-activitylog feed (business edits + all client-scoped actions).
// Update entries show a per-field old → new diff.
function ActivityTab({ businessId }) {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => clearTimeout(t);
  }, [search]);

  const activity = useApi(
    () => getBusinessActivity(businessId, { per_page: 50, ...(debouncedSearch ? { q: debouncedSearch } : {}) }),
    [businessId, debouncedSearch],
  );
  const rows = paginatedRows(activity.data);

  return (
    <div className="col gap-md">
      <div style={{ position: "relative", maxWidth: 360 }}>
        <Icon
          name="search"
          size={13}
          style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-400)", pointerEvents: "none" }}
        />
        <input
          className="input"
          style={{ paddingLeft: 30 }}
          placeholder="Search activity or person…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="card">
        <div className="card-body no-pad">
          {activity.loading ? (
            <div style={{ padding: 16 }}><LoadingBlock rows={5} /></div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon="clock"
              title={debouncedSearch ? "No matching activity" : "No activity yet"}
              hint={debouncedSearch ? "Try a different search." : "Actions on this client will appear here."}
            />
          ) : (
            <div style={{ padding: "8px 16px" }}>
              {rows.map((a) => (
                <div
                  key={a.id}
                  className="row gap-sm"
                  style={{ alignItems: "flex-start", padding: "10px 0", borderBottom: "1px solid var(--border)" }}
                >
                  <div style={{ width: 6, height: 6, borderRadius: 999, marginTop: 7, background: "var(--primary-500)", flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="t-primary" style={{ fontSize: 13 }}>{a.description}</div>
                    <div className="t-muted text-xs" style={{ marginTop: 2 }}>
                      {formatDateTime(a.created_at)}
                      {a.causer ? ` · by ${a.causer}` : ""}
                    </div>
                    {Array.isArray(a.changes) && a.changes.length > 0 && (
                      <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 2 }}>
                        {a.changes.map((c) => (
                          <div
                            key={c.field}
                            className="text-xs"
                            style={{ display: "flex", flexWrap: "wrap", gap: 4, alignItems: "baseline" }}
                          >
                            <span className="t-muted" style={{ fontWeight: 600 }}>{prettyField(c.field)}:</span>
                            <span style={{ textDecoration: "line-through", color: "var(--ink-500)" }}>{fmtActivityValue(c.old)}</span>
                            <Icon name="arrow-right" size={10} style={{ color: "var(--ink-400)" }} />
                            <span className="t-primary">{fmtActivityValue(c.new)}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ImportFieldGroupsDrawer({ open, onClose, businessId, onImported }) {
  const [search, setSearch] = useState("");
  const [picked, setPicked] = useState(null); // selected source business
  const [groupSelection, setGroupSelection] = useState({}); // { groupId: true }
  const [copyValues, setCopyValues] = useState(false);
  const [saving, setSaving] = useState(false);

  const sourcesApi = useApi(
    () =>
      listAllBusinesses({
        per_page: 50,
        exclude_id: businessId,
        ...(search ? { search } : {}),
      }),
    [search, businessId, open],
    { immediate: false },
  );
  const sourceRows = paginatedRows(sourcesApi.data);

  const pickedDetailApi = useApi(
    () =>
      picked
        ? listBusinessFieldGroups(picked.id, { per_page: 100 })
        : Promise.resolve({ data: { data: [] } }),
    [picked?.id],
    { immediate: false },
  );
  const pickedGroups = paginatedRows(pickedDetailApi.data);

  // reset state on open/close
  useEffect(() => {
    if (open) {
      setSearch("");
      setPicked(null);
      setGroupSelection({});
      setCopyValues(false);
      sourcesApi.reload().catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // when search changes (debounced via effect), reload
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => sourcesApi.reload().catch(() => {}), 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // when picked changes, fetch its groups + select all by default
  useEffect(() => {
    if (!picked) {
      setGroupSelection({});
      return;
    }
    pickedDetailApi.reload().then((res) => {
      const rows = paginatedRows(res);
      const allSelected = {};
      rows.forEach((g) => { allSelected[g.id] = true; });
      setGroupSelection(allSelected);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [picked]);

  const selectedIds = Object.entries(groupSelection)
    .filter(([, v]) => v)
    .map(([k]) => Number(k));

  const submit = async () => {
    if (!picked || selectedIds.length === 0) return;
    setSaving(true);
    try {
      const res = await importBusinessFieldGroups(businessId, {
        source_business_id: picked.id,
        copy_values: copyValues,
        group_ids: selectedIds,
      });
      showToast("success", res.message || "Imported");
      onImported?.();
    } catch (err) {
      showApiError(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Import field groups"
      subtitle={
        picked
          ? `From ${picked.business_name} (${picked.client?.name || "—"})`
          : "Pick a source business to copy field groups from"
      }
      wide
      footer={
        <>
          {picked && (
            <button className="btn btn-ghost" onClick={() => setPicked(null)}>
              <Icon name="chevron-left" size={13} /> Change source
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            onClick={submit}
            disabled={saving || !picked || selectedIds.length === 0}
          >
            {saving
              ? "Importing…"
              : `Import ${selectedIds.length} group${selectedIds.length === 1 ? "" : "s"}`}
          </button>
        </>
      }
    >
      {!picked ? (
        <div className="col gap-md">
          <div className="field">
            <label className="field-label">Search businesses</label>
            <input
              className="input"
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Type a business or client name…"
            />
          </div>

          {sourcesApi.loading ? (
            <LoadingBlock rows={5} />
          ) : sourceRows.length === 0 ? (
            <EmptyState icon="building" title="No matches" hint="Try a different search term." />
          ) : (
            <div className="card">
              <div className="card-body no-pad">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Business</th>
                      <th>Client</th>
                      <th className="t-num">Field groups</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sourceRows.map((b) => (
                      <tr
                        key={b.id}
                        className="clickable"
                        onClick={() => b.field_groups_count > 0 && setPicked(b)}
                        style={b.field_groups_count === 0 ? { opacity: 0.6, cursor: "not-allowed" } : null}
                      >
                        <td className="t-primary">{b.business_name}</td>
                        <td className="t-muted">{b.client?.name || "—"}</td>
                        <td className="t-num">
                          {b.field_groups_count > 0 ? (
                            <span className="badge badge-neutral">{b.field_groups_count}</span>
                          ) : (
                            <span className="t-muted text-xs">none</span>
                          )}
                        </td>
                        <td>
                          {b.field_groups_count > 0 ? (
                            <Icon name="chevron-right" size={14} style={{ color: "var(--ink-400)" }} />
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="col gap-md">
          <div
            style={{
              padding: 12,
              background: "var(--surface-2)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              display: "flex",
              alignItems: "center",
              gap: 12,
            }}
          >
            <input
              id="copy-values"
              type="checkbox"
              checked={copyValues}
              onChange={(e) => setCopyValues(e.target.checked)}
            />
            <label htmlFor="copy-values" style={{ flex: 1, cursor: "pointer" }}>
              <div className="t-primary text-sm fw-500">Copy values too</div>
              <div className="t-muted text-xs" style={{ marginTop: 2 }}>
                By default only the field structure is cloned (names, types, required). Tick this to also copy current values — useful for similar businesses with shared credentials.
              </div>
            </label>
          </div>

          {pickedDetailApi.loading ? (
            <LoadingBlock rows={3} height={48} />
          ) : pickedGroups.length === 0 ? (
            <EmptyState icon="folder" title="No field groups in source business" />
          ) : (
            <div className="card">
              <div className="card-head">
                <div className="card-title">Field groups to import</div>
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => {
                    const allOn = pickedGroups.every((g) => groupSelection[g.id]);
                    const next = {};
                    pickedGroups.forEach((g) => { next[g.id] = !allOn; });
                    setGroupSelection(next);
                  }}
                >
                  {pickedGroups.every((g) => groupSelection[g.id]) ? "Unselect all" : "Select all"}
                </button>
              </div>
              <div className="card-body no-pad">
                {pickedGroups.map((g, i) => (
                  <label
                    key={g.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "12px 18px",
                      borderTop: i > 0 ? "1px solid var(--border)" : "none",
                      cursor: "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={!!groupSelection[g.id]}
                      onChange={(e) => setGroupSelection({ ...groupSelection, [g.id]: e.target.checked })}
                    />
                    <div style={{ flex: 1 }}>
                      <div className="t-primary text-sm fw-500">{g.name}</div>
                      <div className="t-muted text-xs" style={{ marginTop: 2 }}>
                        {g.fields_count ?? 0} field{g.fields_count === 1 ? "" : "s"}
                      </div>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}

// Treat values like "example.com" as full URLs by prepending https://. Schemes
// we already understand (http, https, mailto, tel, ftp) are passed through.
// Defensive trim avoids accidental whitespace from breaking href.
function normalizeUrl(raw) {
  const v = (raw || "").trim();
  if (!v) return v;
  if (/^(https?:|mailto:|tel:|ftp:)/i.test(v)) return v;
  return "https://" + v;
}

const REGIME_LABEL = { old: "Old regime", new: "New regime" };
const GST_SCHEME_LABEL = { regular: "Regular", composition: "Composition" };
const GST_FREQUENCY_LABEL = { monthly: "Monthly", quarterly: "Quarterly" };

function ProfileTab({ b, revealed, setRevealed }) {
  const toggle = (k) => setRevealed({ ...revealed, [k]: !revealed[k] });
  return (
    <div className="col gap-md">
      <ProfileSection title="Identification">
        <ProfileRow label="Client ID" value={b.client_id} mono />
        <ProfileRow label="Office File No" value={b.office_file_no} />
        <ProfileRow label="Legal name" value={b.client_name} />
        <ProfileRow label="Trade name" value={b.business_name} />
        <ProfileRow label="MD Mobile" value={b.business_phone} mono />
        <ProfileRow label="Email" value={b.business_email} />
        <ProfileRow label="Concerned person" value={b.concerned_person_name} />
        <ProfileRow label="Concerned person mobile" value={b.concerned_person_phone} mono />
        <ProfileRow label="Address" value={b.address} />
      </ProfileSection>

      <ProfileSection title="Income tax">
        <ProfileRow label="PAN" value={b.pan} mono />
        <SecretRow
          label="IT portal password"
          value={b.it_password}
          shown={!!revealed.it_password}
          onToggle={() => toggle("it_password")}
        />
        <ProfileRow label="ITR type" value={b.itr_type} />
        <ProfileRow label="Regime" value={REGIME_LABEL[b.regime] || b.regime} />
      </ProfileSection>

      <ProfileSection title="GST">
        <ProfileRow label="GSTIN" value={b.gstin} mono />
        <ProfileRow label="State name" value={b.state_name} />
        <ProfileRow label="State code" value={b.state_code} mono />
        <ProfileRow label="GST portal ID" value={b.gst_id} />
        <SecretRow
          label="GST portal password"
          value={b.gst_password}
          shown={!!revealed.gst_password}
          onToggle={() => toggle("gst_password")}
        />
        <ProfileRow label="Filing frequency" value={GST_FREQUENCY_LABEL[b.gst_frequency] || b.gst_frequency} />
        <ProfileRow label="Scheme" value={GST_SCHEME_LABEL[b.gst_scheme] || b.gst_scheme} />
      </ProfileSection>

      <ProfileSection title="Entity">
        <ProfileRow label="Type of entity" value={b.entity_type} />
        <ProfileRow label="Authorised signatory" value={b.authorised_signatory} />
      </ProfileSection>

      <ProfileSection title="TAN / TRACES">
        <ProfileRow label="TAN no." value={b.tan_no} mono />
        <SecretRow
          label="TAN IT password"
          value={b.tan_it_password}
          shown={!!revealed.tan_it_password}
          onToggle={() => toggle("tan_it_password")}
        />
        <ProfileRow label="TRACES ID" value={b.tan_traces_id} />
        <SecretRow
          label="TRACES password"
          value={b.tan_traces_password}
          shown={!!revealed.tan_traces_password}
          onToggle={() => toggle("tan_traces_password")}
        />
      </ProfileSection>
    </div>
  );
}

function ProfileSection({ title, children }) {
  return (
    <div className="card">
      <div className="card-body">
        <div className="t-primary fw-600" style={{ marginBottom: 10 }}>{title}</div>
        <div className="col gap-sm">{children}</div>
      </div>
    </div>
  );
}

function ProfileRow({ label, value, mono = false }) {
  return (
    <div className="row" style={{ gap: 12, alignItems: "baseline" }}>
      {/* min(180px, 38%) so the value column keeps room on phone widths */}
      <div className="t-muted text-sm" style={{ width: "min(180px, 38%)", flexShrink: 0 }}>{label}</div>
      <div className={`t-primary ${mono ? "t-mono" : ""}`} style={{ flex: 1, wordBreak: "break-word" }}>
        {value || <span className="t-muted">—</span>}
      </div>
    </div>
  );
}

function SecretRow({ label, value, shown, onToggle }) {
  const display = !value
    ? <span className="t-muted">—</span>
    : shown
      ? <span className="t-mono">{value}</span>
      : <span className="t-mono">{"•".repeat(Math.min((value || "").length, 12))}</span>;
  return (
    <div className="row" style={{ gap: 12, alignItems: "baseline" }}>
      <div className="t-muted text-sm" style={{ width: "min(180px, 38%)", flexShrink: 0 }}>{label}</div>
      <div className="row" style={{ flex: 1, gap: 8, alignItems: "center" }}>
        <div style={{ flex: 1, wordBreak: "break-all" }}>{display}</div>
        {value && (
          <button className="icon-btn" type="button" onClick={onToggle} title={shown ? "Hide" : "Show"}>
            <Icon name={shown ? "eye-off" : "eye"} size={14} />
          </button>
        )}
      </div>
    </div>
  );
}
